import { useUnsaved } from "@/lib/unsaved";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Save, X, Plus, CalendarDays, Terminal, History, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { RoleSelect } from "@/components/RoleSelect";
import { ChannelSelect } from "@/components/ChannelSelect";
import { Toggle, PageHead } from "@/components/kit";

const STEPS = [
  { cmd: "/announcement template:📅 Live schedule", desc: "Pick a template — General announcement or Live schedule" },
  { cmd: "channel · image · ping · color", desc: "Optional: target channel, upload an image, role to ping, embed color" },
  { cmd: "Fill the pop-up form", desc: "Title, message or one schedule line per day (e.g. Mon 7PM - Valorant), timezone, stream link, image URL" },
  { cmd: "Preview → Post / Edit / Cancel", desc: "Only you see the preview. Post sends it to the channel with a Watch Live button" },
  { cmd: "✏️ Edit · right-click → Apps → Edit announcement", desc: "Fix typos, links, image or color on a posted announcement — it updates in place and never re-pings" },
];

const SAMPLE = [
  ["Monday", "7PM", "Valorant"], ["Wednesday", "8PM", "Just Chatting"], ["Friday", "9PM", "Community games"],
  ["Saturday", "6PM", "Minecraft"], ["Sunday", null, null],
];

function RoleList({ guildId, roles, onChange }) {
  const [pick, setPick] = useState("");
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2" data-testid="ann-roles-list">
        {roles.map((r) => (
          <span key={r} className="flex items-center gap-1 bg-zinc-800 text-sm rounded-full pl-3 pr-1.5 py-1">
            {r}
            <button data-testid={`ann-role-remove-${r}`} onClick={() => onChange(roles.filter((x) => x !== r))} className="hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
          </span>
        ))}
        {!roles.length && <span className="text-xs text-zinc-600">No extra roles yet.</span>}
      </div>
      <div className="flex gap-2">
        <RoleSelect guildId={guildId} value={pick} onChange={setPick} testid="ann-role-input" placeholder="— add announcer role —" className="flex-1 min-w-0" />
        <button data-testid="ann-role-add" onClick={() => { if (pick && !roles.includes(pick)) onChange([...roles, pick]); setPick(""); }} className="bg-zinc-800 hover:bg-zinc-700 rounded-md px-3 flex items-center"><Plus className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

function timeAgo(iso) {
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso + (iso.endsWith("Z") ? "" : "Z")).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function RecentEdits({ guildId }) {
  const [edits, setEdits] = useState(null);
  useEffect(() => { api.get(`/guilds/${guildId}/announcements/edits?limit=10`).then((r) => setEdits(r.data)).catch(() => setEdits([])); }, [guildId]);
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6" data-testid="ann-edits">
      <h3 className="font-head font-semibold mb-4 flex items-center gap-2"><History className="w-4 h-4 text-volt" /> Recent edits</h3>
      {edits === null && <p className="text-sm text-zinc-500">Loading…</p>}
      {edits?.length === 0 && <p className="text-sm text-zinc-500" data-testid="ann-edits-empty">No edits yet. When staff fix a posted announcement, it shows up here.</p>}
      <div className="space-y-2">
        {edits?.map((e, i) => (
          <div key={`${e.message_id}-${e.at}`} data-testid={`ann-edit-row-${i}`} className="flex items-center gap-3 bg-black/30 rounded-md p-3">
            <span className="text-lg">{e.kind === "schedule" ? "📅" : "📢"}</span>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-zinc-200 truncate">{e.title || "Untitled"}</div>
              <div className="text-xs text-zinc-500">Edited by <span className="text-zinc-300">{e.editor}</span> · {timeAgo(e.at)}</div>
            </div>
            {e.jump_url && <a data-testid={`ann-edit-jump-${i}`} href={e.jump_url} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-volt"><ExternalLink className="w-4 h-4" /></a>}
          </div>
        ))}
      </div>
    </div>
  );
}

function SchedulePreview({ color, label }) {
  return (
    <div data-testid="ann-preview" className="force-dark bg-[#2b2d31] rounded-md flex overflow-hidden">
      <div className="w-1 shrink-0" style={{ background: color }} />
      <div className="p-4 flex-1 min-w-0">
        <div className="text-xs text-zinc-300 font-semibold mb-1">Your Server</div>
        <div className="font-semibold text-white">📅 Weekly Stream Schedule</div>
        <div className="text-sm text-zinc-300 mt-1">🕒 All times in <b>EST</b></div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
          {SAMPLE.map(([d, t, g]) => (
            <div key={d} className="text-sm">
              <div className="font-semibold text-white">🗓️ {d}</div>
              <div className="text-zinc-300">{t ? <>⏰ <b>{t}</b><br />🎮 {g}</> : "😴 Day off"}</div>
            </div>
          ))}
        </div>
        <div className="text-[11px] text-zinc-500 mt-3">Posted by you · Today</div>
        <div className="flex gap-2 mt-3">
          <span className="bg-[#4e5058] text-white text-sm rounded px-3 py-1.5">🔴 {label || "Watch Live"} ↗</span>
          <span className="bg-[#4e5058] text-white text-sm rounded px-3 py-1.5">✏️ Edit</span>
        </div>
      </div>
    </div>
  );
}

export default function Announcements() {
  const { guildId } = useGuild();
  const [cfg, setCfg] = useState(null);
  const save = useUnsaved(cfg, setCfg, () => saveRaw());
  useEffect(() => { api.get(`/guilds/${guildId}/modules/announcements`).then((r) => setCfg(r.data)); }, [guildId]);
  if (!cfg) return <p className="text-zinc-500">Loading…</p>;
  const c = cfg.config;
  const set = (k, v) => setCfg({ ...cfg, config: { ...c, [k]: v } });
  const valid = /^#[0-9a-fA-F]{6}$/.test(c.default_color || "");
  const saveRaw = async () => {
    if (!valid) { toast.error("Brand color must be a hex value like #0EA5E9"); return; }
    await api.put(`/guilds/${guildId}/modules/announcements`, { enabled: cfg.enabled, config: c });
    toast.success("Announcement settings saved");
  };

  return (
    <div data-testid="announcements-page">
      <PageHead title="Announcements" desc="Let staff post polished announcements and live-stream schedules with /announcement.">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm"><span className="text-zinc-400">Enabled</span><Toggle testid="ann-enabled" checked={cfg.enabled} onChange={(v) => setCfg({ ...cfg, enabled: v })} /></div>
          <button data-testid="ann-save" onClick={save} className="bg-volt text-volt-ink font-semibold rounded-md px-5 py-2 hover:bg-volt-hover flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
        </div>
      </PageHead>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-5 h-fit">
          <h3 className="font-head font-semibold flex items-center gap-2"><Megaphone className="w-4 h-4 text-volt" /> Settings</h3>
          <div><label className="text-xs uppercase tracking-wider text-zinc-500">Who can post</label>
            <div className="mt-2"><RoleList guildId={guildId} roles={c.allowed_roles || []} onChange={(v) => set("allowed_roles", v)} /></div>
            <p className="text-xs text-zinc-500 mt-2">Members with Manage Server can always post.</p></div>
          <div><label className="text-xs uppercase tracking-wider text-zinc-500">Default channel</label>
            <ChannelSelect guildId={guildId} value={c.default_channel || ""} onChange={(v) => set("default_channel", v)} testid="ann-channel" placeholder="— channel where the command is used —" className="mt-1 w-full" /></div>
          <div><label className="text-xs uppercase tracking-wider text-zinc-500">Default ping role</label>
            <RoleSelect guildId={guildId} value={c.default_ping || ""} onChange={(v) => set("default_ping", v)} testid="ann-default-ping" placeholder="— no ping by default —" className="mt-1 w-full" />
            <p className="text-xs text-zinc-500 mt-2">Used when the command's <code className="text-volt">ping</code> option is left empty. Staff can switch it off in the preview.</p></div>
          <div><label className="text-xs uppercase tracking-wider text-zinc-500">Brand color</label>
            <div className="mt-1 flex gap-2">
              <input data-testid="ann-color-picker" type="color" value={valid ? c.default_color : "#0EA5E9"} onChange={(e) => set("default_color", e.target.value)} className="h-10 w-12 bg-black border border-zinc-800 rounded-md cursor-pointer" />
              <input data-testid="ann-color-hex" value={c.default_color || ""} onChange={(e) => set("default_color", e.target.value)} className={`flex-1 min-w-0 bg-black border rounded-md px-3 py-2 font-mono text-sm outline-none ${valid ? "border-zinc-800 focus:border-volt" : "border-red-500"}`} />
            </div></div>
          <div><label className="text-xs uppercase tracking-wider text-zinc-500">Link button label</label>
            <input data-testid="ann-watch-label" maxLength={80} value={c.watch_label || ""} onChange={(e) => set("watch_label", e.target.value)} placeholder="Watch Live" className="mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt" /></div>
        </div>
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h3 className="font-head font-semibold mb-4 flex items-center gap-2"><Terminal className="w-4 h-4 text-volt" /> How it works</h3>
            <div className="space-y-2">
              {STEPS.map((x, i) => (
                <div key={x.cmd} className="flex items-start gap-3 bg-black/30 rounded-md p-3">
                  <span className="text-xs text-zinc-500 font-mono pt-0.5">{i + 1}</span>
                  <div className="min-w-0"><code className="text-volt text-sm font-mono break-words">{x.cmd}</code>
                    <div className="text-xs text-zinc-500 mt-0.5">{x.desc}</div></div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h3 className="font-head font-semibold mb-4 flex items-center gap-2"><CalendarDays className="w-4 h-4 text-volt" /> Live schedule preview</h3>
            <SchedulePreview color={valid ? c.default_color : "#0EA5E9"} label={c.watch_label} />
          </div>
          <RecentEdits guildId={guildId} />
        </div>
      </div>
    </div>
  );
}
